import CommandCenterService from "../../../service/CommandCenterService";
import { getFormatedTime, showToastMessage } from "../../../utils/commonFunctions";
import { KeyDetailType, SecurityConfigDetailsType } from "./types";

export const SECRET_KEY_TASK_TYPES = ['upload_activate_secret_key', 'upload_deactivate_secret_key', 'activate_secret_key', 'deactivate_secret_key']


export function getSecurityConfigDetails(data: SecurityConfigDetailsType | null): KeyDetailType[] {
    if (!data || !data?.authKeys || !data?.encryptionKeys) return []
    const authDetails: KeyDetailType[] = data?.authKeys.map((item, index) => ({
        name: `Authentication Key ${index + 1}`,
        key: item,
        active: data?.authKeyActive[index] ?? false,
        type: "password",
        modified: data?.authKeyUpdatedAt[index] ? getFormatedTime(data.authKeyUpdatedAt[index]) : "",
    }));

    const encryptionDetails: KeyDetailType[] = data?.encryptionKeys.map((item, index) => ({
        name: `Encryption Key ${index + 1}`,
        key: item,
        active: data?.encryptionKeyActive[index] ?? false,
        type: "password",
        modified: data?.encryptionKeyUpdatedAt[index] ? getFormatedTime(data.encryptionKeyUpdatedAt[index]) : "",
    }));
    return [...authDetails, ...encryptionDetails];
}

export const getSecurityKeyInputNames = (data: SecurityConfigDetailsType): { value: string, label: string }[] => {
    const authDetails: { value: string, label: string }[] = data.authKeys.map((item, index) => {
        return {
            value: `authentication_${index + 1}`,
            label: `Authentication ${index + 1}`
        }
    });
    const encryptionDetails: { value: string, label: string }[] = data.encryptionKeys.map((item, index) => {
        return {
            value: `encryption_${index + 1}`,
            label: `Encryption ${index + 1}`
        }
    });
    return [...authDetails, ...encryptionDetails];
}

export function revertSecurityConfigDetails(data: KeyDetailType[]) {
    const encryptionKeys: string[] = [];
    const authKeys: string[] = [];

    const encryptionKeyActive: Record<string, boolean> = {};
    const authKeyActive: Record<string, boolean> = {};

    data.forEach((item, index) => {
        const isAuth = item.name.toLowerCase().includes("authentication");
        const isEncryption = item.name.toLowerCase().includes("encryption");

        if (isAuth) {
            const keyIndex = authKeys.length;
            authKeys.push(item.key);
            authKeyActive[keyIndex] = item.active;
        }

        if (isEncryption) {
            const keyIndex = encryptionKeys.length;
            encryptionKeys.push(item.key);
            encryptionKeyActive[keyIndex] = item.active;
        }
    });

    return {
        encryptionKeys,
        authKeys,
        encryptionKeyActive,
        authKeyActive,
    };
}

export const getAllEncriptionNames = (
    data: SecurityConfigDetailsType
): {
    value: string;
    label: string;
    prevIndex: number;
    nextIndex: number;
    id: number;
}[] => {
    const keys = Object.keys(data.securityKeyIdx);
    const encryptionOptions = keys.map((key) => ({
        value: `security_key_${key}`,
        label: `Security Key ${key}`,
    }));
    const total = encryptionOptions.length;
    return encryptionOptions.map((item, index) => ({
        ...item,
        prevIndex: Number(keys[(index - 1 + total) % total]),
        nextIndex: Number(keys[(index + 1) % total]),
        id: Number(index)
    }));
};

export const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, inputType: 'key' | 'seed') => {
    const allowedControlKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Home",
        "End",
        "Meta",
        "Control"
    ];

    if (allowedControlKeys.includes(e.key)) return;

    const currentValue = e.currentTarget.value;
    const selectionStart = e.currentTarget.selectionStart ?? currentValue.length;
    const selectionEnd = e.currentTarget.selectionEnd ?? currentValue.length;
    const selectedLength = selectionEnd - selectionStart;
    const resultLength = currentValue.length - selectedLength + 1;

    if (!/^[a-zA-Z0-9]$/.test(e.key)) {
        e.preventDefault();
        showToastMessage('info', 'Only alphanumeric values are allowed')
    } else if (resultLength > 64) {
        e.preventDefault();
        showToastMessage('info', 'Maximum 64 alphanumeric characters are allowed')
    }
    // This code may be used in the future.
    // if (inputType === 'key') {
    // } else {
    //     if (!/^[0-9]$/.test(e.key)) {
    //         e.preventDefault();
    //         showToastMessage('info', 'Only numeric values are allowed')
    //     }
    // }
}

export const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, onChange: (value: string) => void) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const sanitized = pasted.replace(/[^a-zA-Z0-9]/g, '');

    const input = e.currentTarget;
    const currentValue = input.value;
    const selectionStart = input.selectionStart ?? currentValue.length;
    const selectionEnd = input.selectionEnd ?? currentValue.length;
    const newValue = (currentValue.slice(0, selectionStart) + sanitized + currentValue.slice(selectionEnd)).slice(0, 64);

    if (sanitized !== pasted) {
        showToastMessage('info', 'Only alphanumeric values are allowed')
    } else if (newValue.length < currentValue.slice(0, selectionStart).length + sanitized.length) {
        showToastMessage('info', 'Maximum 64 alphanumeric characters are allowed')
    }

    onChange(newValue);
}

export const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value.length < 64) {
        showToastMessage('info', 'Key must be 64 characters long')
    }
}

export const getSecurityConfiguration = async (satelliteId: string): Promise<SecurityConfigDetailsType | null> => {
    try {
        const response = await CommandCenterService.getSecurityConfig(satelliteId);
        const data = response?.data?.data ?? null;
        return data;
    } catch (error) {
        console.error("Failed to fetch security configuration:", error);
        return null;
    }
};

export const DefaultFormDetails = [{
    name: 'Authentication Key 1',
    key: '',
    active: false,
    type: 'password',
    modified: '03-04-2027 21:18:09 UTC'
},
{
    name: 'Authentication Key 2',
    key: '',
    active: false,
    type: 'text',
    modified: '03-04-2027 21:18:09 UTC'
},
{
    name: 'Encryption Key 1',
    key: '',
    active: false,
    type: 'text',
    modified: '03-04-2027 21:18:09 UTC'
},
{
    name: 'Encryption Key 2',
    key: '',
    active: false,
    type: 'text',
    modified: '03-04-2027 21:18:09 UTC'
},
{
    name: 'Encryption Key 3',
    key: '',
    active: false,
    type: 'text',
    modified: '03-04-2027 21:18:09 UTC'
}]